import type { BuildingFootprintTemplate } from './building-footprint-template.helpers';

export const BUILDING_FOOTPRINT_PREVIEW_WIDTH = 132;
export const BUILDING_FOOTPRINT_PREVIEW_HEIGHT = 112;

/** Shared black fill and outline styling for panel and map preset previews. */
export const BUILDING_FOOTPRINT_PREVIEW_STYLE = {
  fillOpacity: 0.05,
  activeFillOpacity: 0.1,
  strokeOpacity: 0.3,
  strokeWidth: 1,
  /** Resolved selected-panel colors sampled from the approved reference. */
  mapFillColor: '#d3d1cf',
  mapStrokeColor: '#939290',
  mapOpacity: 0.9,
};

const PREVIEW_PADDING = 8;
const PREVIEW_CORNER_RADIUS = 2;
const PREVIEW_TARGET_AREA = 3600;

type PreviewPoint = [number, number];

const getPolygonArea = (points: readonly PreviewPoint[]): number =>
  Math.abs(
    points.reduce((sum, point, index) => {
      const next = points[(index + 1) % points.length];
      if (!next) return sum;
      return sum + point[0] * next[1] - next[0] * point[1];
    }, 0) / 2,
  );

/**
 * Fit every preset into the same panel card.
 * Visible area, with courtyard holes removed, is equalized before the card clips it.
 * Panel coordinates flip Y so the thumbnail matches the north-up template.
 */
const normalizePreviewPolygons = (
  template: BuildingFootprintTemplate,
): PreviewPoint[][] => {
  if (!template.points) return [];
  const polygons = [template.points, ...(template.cutouts ?? [])];
  const outer = polygons[0] ?? [];
  const xs = outer.map(([x]) => x);
  const ys = outer.map(([, y]) => -y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const width = Math.max(1, maxX - minX);
  const height = Math.max(1, maxY - minY);
  const fitScale = Math.min(
    (BUILDING_FOOTPRINT_PREVIEW_WIDTH - PREVIEW_PADDING * 2) / width,
    (BUILDING_FOOTPRINT_PREVIEW_HEIGHT - PREVIEW_PADDING * 2) / height,
  );
  const [outerArea = 0, ...cutoutAreas] = polygons.map((points) =>
    getPolygonArea(points.map(([x, y]): PreviewPoint => [x, -y])),
  );
  const polygonArea = Math.max(
    1,
    outerArea - cutoutAreas.reduce((sum, area) => sum + area, 0),
  );
  const equalAreaScale = Math.sqrt(
    PREVIEW_TARGET_AREA / Math.max(1, polygonArea),
  );
  const scale = Math.min(fitScale, equalAreaScale);
  const offsetX =
    (BUILDING_FOOTPRINT_PREVIEW_WIDTH - width * scale) / 2 - minX * scale;
  const offsetY =
    (BUILDING_FOOTPRINT_PREVIEW_HEIGHT - height * scale) / 2 - minY * scale;
  return polygons.map((points) =>
    points.map(([x, y]): PreviewPoint => [
      x * scale + offsetX,
      -y * scale + offsetY,
    ]),
  );
};

const moveToward = (
  from: PreviewPoint,
  to: PreviewPoint,
  distance: number,
): PreviewPoint => {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy);
  if (!length) return from;
  const ratio = Math.min(distance, length / 2) / length;
  return [from[0] + dx * ratio, from[1] + dy * ratio];
};

const formatPreviewPoint = ([x, y]: PreviewPoint): string =>
  `${x.toFixed(3)} ${y.toFixed(3)}`;

/** Rounded path used only by the panel preview; placement keeps exact points. */
const getRoundedPolygonPath = (points: readonly PreviewPoint[]): string => {
  if (points.length < 3) return '';
  const corners = points.flatMap((point, index) => {
    const previous = points[(index - 1 + points.length) % points.length];
    const next = points[(index + 1) % points.length];
    if (!previous || !next) return [];
    return [
      {
        point,
        start: moveToward(point, previous, PREVIEW_CORNER_RADIUS),
        end: moveToward(point, next, PREVIEW_CORNER_RADIUS),
      },
    ];
  });
  const [first, ...rest] = corners;
  if (!first) return '';
  return [
    `M ${formatPreviewPoint(first.start)}`,
    `Q ${formatPreviewPoint(first.point)} ${formatPreviewPoint(first.end)}`,
    ...rest.flatMap(({ point, start, end }) => [
      `L ${formatPreviewPoint(start)}`,
      `Q ${formatPreviewPoint(point)} ${formatPreviewPoint(end)}`,
    ]),
    `L ${formatPreviewPoint(first.start)} Z`,
  ].join(' ');
};

/** Panel thumbnail path for one footprint preset, including courtyard holes. */
export const getBuildingFootprintPreviewPath = (
  template: BuildingFootprintTemplate,
): string =>
  normalizePreviewPolygons(template).map(getRoundedPolygonPath).join(' ');
